import type { TeactNode } from '../../lib/teact/teact';
import React, { memo, useEffect, useRef, useState } from '../../lib/teact/teact';
import { getActions, getGlobal, withGlobal } from '../../global';

import {
  AUTO_CONFIRM_DURATION_MINUTES,
  PIN_LENGTH,
  WRONG_ATTEMPTS_BEFORE_LOG_OUT_SUGGESTION,
} from '../../config';
import { selectIsBiometricAuthEnabled } from '../../global/selectors';
import { selectAuthUsageCountRequest, selectEnclaveToken } from '../../global/selectors/enclave';
import { getDoesUsePinPad, getIsFaceIdAvailable, getIsTouchIdAvailable } from '../../util/biometrics';
import buildClassName from '../../util/buildClassName';
import captureKeyboardListeners from '../../util/captureKeyboardListeners';
import { stopEvent } from '../../util/domEvents';
import { toNativeDigits } from '../../util/nativeDigits';
import { pause } from '../../util/schedulers';
import { createSignal } from '../../util/signals';
import { enclave } from '../../enclave';
import { ANIMATED_STICKERS_PATHS } from './helpers/animatedAssets';

import { useDeviceScreen } from '../../hooks/useDeviceScreen';
import useEffectOnce from '../../hooks/useEffectOnce';
import useFlag from '../../hooks/useFlag';
import useFocusAfterAnimation from '../../hooks/useFocusAfterAnimation';
import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import { useMatchCount } from '../../hooks/useMatchCount';
import useTimeout from '../../hooks/useTimeout';
import useToggleClass from '../../hooks/useToggleClass';

import LogOutModal from '../main/modals/LogOutModal';
import AnimatedIconWithPreview from './AnimatedIconWithPreview';
import Button from './Button';
import Checkbox from './Checkbox';
import Input from './Input';
import PinPad from './PinPad';

import modalStyles from './Modal.module.scss';
import styles from './PasswordForm.module.scss';

type OperationType = 'transfer' | 'sending' | 'staking' | 'unstaking'
  | 'unfreeze' | 'passcode' | 'unlock' | 'claim' | 'turnOnBiometrics' | 'mintCard';

interface OwnProps {
  isActive: boolean;
  isLoading?: boolean;
  operationType?: OperationType;
  cancelLabel?: string;
  submitLabel?: string | TeactNode[];
  stickerSize?: number;
  placeholder?: string;
  error?: string;
  pinPadTitle?: string;
  help?: string;
  resetStateDelayMs?: number;
  containerClassName?: string;
  pinPadClassName?: string;
  withCloseButton?: boolean;
  isFullWidthButton?: boolean;
  children?: TeactNode;
  noAnimatedIcon?: boolean;
  inputWrapperClassName?: string;
  forceBiometricsInMain?: boolean;
  noBiometrics?: boolean;
  errorClassName?: string;
  noAutoConfirm?: boolean;
  /**
   * Secret reads this flow needs beyond the single one every operation gets. A usage is spent when
   * the private key is read, not when an API call is made, so only a flow that reads the key in more
   * than one step has to ask for more; the session dies on the last read it granted. A flow that has
   * to name the largest count it might need gives back what it did not spend when it ends.
   */
  extraAuthUsages?: number;
  onCancel?: NoneToVoidFunction;
  onUpdate?: NoneToVoidFunction;
  onAuthorize: (enclaveToken: string) => void;
  onError?: (error?: string) => void;
}

interface StateProps {
  isPasswordNumeric?: boolean;
  isAutoConfirmEnabled?: boolean;
  enclaveSessionValidUntil?: number;
  isBiometricAuthEnabled: boolean;
  authUsageCountRequest?: number;
}

const STICKER_SIZE = 180;
const APPEAR_ANIMATION_DURATION_MS = 300;

const [getHandleBiometricsSignal, setHandleBiometricsSignal] = createSignal(Date.now());

export function triggerPasswordFormHandleBiometrics(e?: MouseEvent | KeyboardEvent) {
  if (e) {
    stopEvent(e);
  }
  setHandleBiometricsSignal(Date.now());
}

function PasswordForm({
  isActive,
  isLoading,
  operationType,
  isPasswordNumeric,
  isBiometricAuthEnabled: isBiometricAuthEnabledProp,
  cancelLabel,
  submitLabel,
  stickerSize = STICKER_SIZE,
  placeholder = getDoesUsePinPad() ? 'Enter your passcode' : 'Enter your password',
  error,
  pinPadTitle,
  help,
  resetStateDelayMs,
  containerClassName,
  pinPadClassName,
  children,
  withCloseButton,
  isFullWidthButton,
  noAnimatedIcon,
  inputWrapperClassName,
  forceBiometricsInMain,
  noBiometrics,
  errorClassName,
  isAutoConfirmEnabled,
  enclaveSessionValidUntil,
  noAutoConfirm,
  authUsageCountRequest,
  extraAuthUsages,
  onUpdate,
  onCancel,
  onAuthorize,
  onError,
}: OwnProps & StateProps) {
  const {
    setIsAutoConfirmEnabled,
    setEnclaveSession,
    upgradeMultichainAccounts,
  } = getActions();

  const lang = useLang();

  const isBiometricAuthEnabled = isBiometricAuthEnabledProp && !noBiometrics;

  const inputRef = useRef<HTMLInputElement>();
  const [inputValue, setInputValue] = useState<string>('');
  const [localError, setLocalError] = useState<string>('');
  const { isSmallHeight, isPortrait } = useDeviceScreen();
  const withAutoConfirm = useCanAutoConfirm(enclaveSessionValidUntil, noAutoConfirm);
  /**
   * Whether this entry opens the auto-confirm window - not the question `noAutoConfirm` answers, which
   * is whether this screen may be skipped. Tying them together left the transfer and dApp flows able to
   * spend the window and unable to open it. Biometrics rule it out, read from the prop because
   * `isBiometricAuthEnabled` is off wherever a screen passes `noBiometrics` on an account that has them,
   * and the screen that turns biometrics on is a moment away from ruling it out too.
   */
  const canArmAutoConfirm = !isBiometricAuthEnabledProp && operationType !== 'turnOnBiometrics';
  const isLongSession = canArmAutoConfirm && Boolean(isAutoConfirmEnabled);
  const isSubmitDisabled = !inputValue.length && !withAutoConfirm;
  const canUsePinPad = getDoesUsePinPad();
  const [isLogOutModalOpened, openLogOutModal, closeLogOutModal] = useFlag(false);
  const shouldSuggestLogout = useMatchCount(!!error || !!localError, WRONG_ATTEMPTS_BEFORE_LOG_OUT_SUGGESTION);
  const isAuthorizingRef = useRef(false);

  const extraUsages = (authUsageCountRequest ?? 0) + (extraAuthUsages ?? 0);
  const usageCount = extraUsages ? 1 + extraUsages : undefined;

  const handleAuthorized = useLastCallback((enclaveToken: string) => {
    // The handler is a no-op when there is nothing to upgrade
    upgradeMultichainAccounts({ enclaveToken });
    onAuthorize(enclaveToken);
  });

  useEffect(() => {
    if (isActive) {
      setLocalError('');
      setInputValue('');
      isAuthorizingRef.current = false;
    }
  }, [isActive]);

  const handleSubmit = useLastCallback(async (pin?: string) => {
    // Prevent double authorization (e.g., race between PIN input and biometrics)
    if (isAuthorizingRef.current || isLoading) return;
    isAuthorizingRef.current = true;

    if (withAutoConfirm) {
      handleAuthorized(selectEnclaveToken(getGlobal())!);
      return;
    }

    const password = pin ?? inputValue;

    // Normal authorization
    const enclaveSession = await enclave.authorize('passcode', isLongSession, password, usageCount);

    if (!enclaveSession) {
      isAuthorizingRef.current = false;
      const errorMessage = 'Wrong password, please try again.';
      setLocalError(errorMessage);
      onError?.(errorMessage);
      return;
    }

    setEnclaveSession(enclaveSession);
    handleAuthorized(enclaveSession.token);
  });

  const handleBiometrics = useLastCallback(async () => {
    // Prevent double authorization (e.g., race between PIN input and biometrics)
    if (isAuthorizingRef.current || isLoading) return;
    isAuthorizingRef.current = true;

    try {
      setLocalError('');

      const enclaveSession = await enclave.authorize('biometric', false, undefined, usageCount);
      if (!enclaveSession) {
        isAuthorizingRef.current = false;
        const errorMessage = 'Biometric confirmation failed';
        setLocalError(errorMessage);
        onError?.(errorMessage);
        return;
      }

      setEnclaveSession(enclaveSession);
      handleAuthorized(enclaveSession.token);
    } catch (err: any) {
      isAuthorizingRef.current = false;
      const errorMessage = err.message || lang('Something went wrong.');
      setLocalError(errorMessage);
      onError?.(errorMessage);
    }
  });

  useEffect(() => {
    if (!isActive || !isBiometricAuthEnabled || withAutoConfirm) {
      return;
    }

    void pause(APPEAR_ANIMATION_DURATION_MS).then(handleBiometrics);
  }, [
    forceBiometricsInMain, handleBiometrics, isActive, isBiometricAuthEnabled, withAutoConfirm,
  ]);

  useEffectOnce(() => {
    return getHandleBiometricsSignal.subscribe(() => {
      void handleBiometrics();
    });
  });

  useFocusAfterAnimation(inputRef, !isActive || isBiometricAuthEnabled);

  useToggleClass({ className: 'is-password-form-visible', isActive });

  const handleClearError = useLastCallback(() => {
    setLocalError('');
    onUpdate?.();
    onError?.(undefined);
  });

  const handleInput = useLastCallback((value: string) => {
    setInputValue(value);
    handleClearError();
  });

  const handleAutoConfirmChange = useLastCallback((isEnabled: boolean) => {
    setIsAutoConfirmEnabled({ isEnabled });
  });

  const handleOpenLogOutModal = useLastCallback((e: React.MouseEvent) => {
    stopEvent(e);
    openLogOutModal();
  });

  useEffect(() => {
    return isSubmitDisabled || isLoading
      ? undefined
      : captureKeyboardListeners({ onEnter: () => handleSubmit() });
  }, [handleSubmit, isLoading, isSubmitDisabled]);

  function getPinPadTitle() {
    switch (operationType) {
      case 'unfreeze':
        return 'Confirm Unfreezing';
      case 'passcode':
      case 'turnOnBiometrics':
        return 'Confirm Passcode';
      case 'transfer':
      case 'sending':
        return 'Confirm Sending';
      case 'staking':
        return 'Confirm Staking';
      case 'unstaking':
        return 'Confirm Unstaking';
      case 'unlock':
        return undefined;
      case 'claim':
        return 'Confirm Rewards Claim';
      case 'mintCard':
        return 'Confirm Upgrading';
      default:
        return 'Confirm Action';
    }
  }

  const shouldRenderFullWidthButton = operationType === 'unlock';
  const footerButtonsClassName = buildClassName(
    modalStyles.footerButtons,
    shouldRenderFullWidthButton && modalStyles.footerButtonFullWidth,
  );

  function renderFooterButtons() {
    return (
      <div className={footerButtonsClassName}>
        {onCancel && (
          <Button
            isLoading={isLoading && isBiometricAuthEnabled}
            isDisabled={isLoading && !isBiometricAuthEnabled}
            className={modalStyles.buttonHalfWidth}
            onClick={onCancel}
          >
            {cancelLabel || lang('Cancel')}
          </Button>
        )}
        {isBiometricAuthEnabled && Boolean(localError) && (
          <Button
            isPrimary
            isLoading={isLoading}
            isDisabled={isLoading}
            className={modalStyles.buttonHalfWidth}
            onClick={!isLoading ? handleBiometrics : undefined}
            shouldStopPropagation
          >
            {lang('Try Again')}
          </Button>
        )}
        {(!isBiometricAuthEnabled || withAutoConfirm) && (
          <Button
            isPrimary
            isLoading={isLoading}
            isDisabled={isSubmitDisabled}
            className={(shouldRenderFullWidthButton || isFullWidthButton)
              ? modalStyles.buttonFullWidth : modalStyles.buttonHalfWidth}
            onClick={!isLoading ? handleSubmit : undefined}
          >
            {submitLabel || lang('Send')}
          </Button>
        )}
      </div>
    );
  }

  function renderAutoConfirmCheckbox() {
    return (
      <Checkbox
        checked={!!isAutoConfirmEnabled}
        onChange={handleAutoConfirmChange}
        className={styles.autoConfirmCheckbox}
      >
        {toNativeDigits(lang('Remember for %minutes% minutes', AUTO_CONFIRM_DURATION_MINUTES) as string)}
      </Checkbox>
    );
  }

  // Offered exactly where it can take effect: the checkbox and the window read one signal, so a
  // screen cannot show the option and then refuse to act on it
  const shouldRenderAutoConfirmCheckbox = canArmAutoConfirm;

  if (canUsePinPad) {
    const hasError = Boolean(localError || error);
    const title = getPinPadTitle();
    const actionName = lang(
      !isBiometricAuthEnabled
        ? 'Enter code'
        : getIsFaceIdAvailable()
          ? 'Enter code or use Face ID'
          : getIsTouchIdAvailable()
            ? 'Enter code or use Touch ID'
            : 'Enter code or use biometrics',
    );

    const content = (
      <>
        {withCloseButton && (
          <Button
            isRound
            className={buildClassName(modalStyles.closeButton, styles.closeButton)}
            ariaLabel={lang('Close')}
            onClick={onCancel}
          >
            <i className={buildClassName(modalStyles.closeIcon, 'icon-close')} aria-hidden />
          </Button>
        )}
        <div className={styles.pinPadHeader}>
          {isPortrait && !noAnimatedIcon && (
            <AnimatedIconWithPreview
              play={isActive}
              tgsUrl={ANIMATED_STICKERS_PATHS.guard}
              previewUrl={ANIMATED_STICKERS_PATHS.guardPreview}
              noLoop={false}
              nonInteractive
            />
          )}
          {!isSmallHeight && title && <div className={styles.title}>{lang(title)}</div>}
          {children}
        </div>

        {withAutoConfirm ? renderFooterButtons() : (
          <PinPad
            isActive={isActive}
            title={lang(hasError
              ? (localError || error!)
              : (pinPadTitle || (isSmallHeight && title ? title : actionName)),
            )}
            type={hasError ? 'error' : undefined}
            length={PIN_LENGTH}
            resetStateDelayMs={resetStateDelayMs}
            value={inputValue}
            topContent={shouldRenderAutoConfirmCheckbox ? renderAutoConfirmCheckbox() : undefined}
            className={pinPadClassName}
            onBiometricsClick={isBiometricAuthEnabled ? handleBiometrics : undefined}
            onLogOutClick={operationType === 'unlock' ? openLogOutModal : undefined}
            onChange={setInputValue}
            onClearError={handleClearError}
            onSubmit={handleSubmit}
          />
        )}
        {operationType === 'unlock' && (
          <LogOutModal isOpen={isLogOutModalOpened} onClose={closeLogOutModal} isInAppLock />
        )}
      </>
    );

    return withAutoConfirm ? (
      <div className={modalStyles.transitionContent}>
        {content}
      </div>
    ) : content;
  }

  function renderBiometricPrompt() {
    const renderingError = localError || error;
    if (renderingError) {
      return (
        <div className={styles.error}>{lang(renderingError)}</div>
      );
    }

    return (
      <div className={styles.verify}>
        {lang(operationType === 'transfer'
          ? 'Please confirm transfer using biometrics' : 'Please confirm action using biometrics')}
      </div>
    );
  }

  function renderPasswordForm() {
    const renderingError = error || localError;

    return (
      <>
        <Input
          ref={inputRef}
          type="password"
          isRequired
          id="first-password"
          wrapperClassName={inputWrapperClassName}
          errorClassName={errorClassName}
          inputMode={isPasswordNumeric ? 'numeric' : undefined}
          error={renderingError ? lang(renderingError) : undefined}
          placeholder={lang(placeholder)}
          value={inputValue}
          onInput={handleInput}
          maxLength={isPasswordNumeric ? PIN_LENGTH : undefined}
        />
        {help && !error && (
          <div className={styles.label}>{help}</div>
        )}
        {shouldRenderAutoConfirmCheckbox && renderAutoConfirmCheckbox()}
      </>
    );
  }

  return (
    <div className={buildClassName(modalStyles.transitionContent, containerClassName)}>
      {!noAnimatedIcon && (
        <AnimatedIconWithPreview
          tgsUrl={ANIMATED_STICKERS_PATHS.holdTon}
          previewUrl={ANIMATED_STICKERS_PATHS.holdTonPreview}
          play={isActive}
          size={stickerSize}
          nonInteractive
          noLoop={false}
          className={styles.sticker}
        />
      )}

      {children}

      {!withAutoConfirm && (isBiometricAuthEnabled ? renderBiometricPrompt() : renderPasswordForm())}

      {operationType === 'unlock' && (
        <div className={buildClassName(styles.logOutWrapper, !shouldSuggestLogout && styles.logOutWrapperHidden)}>
          {lang('Can\'t confirm?')}
          <span
            role="button"
            tabIndex={0}
            className={styles.logOutButton}
            onClick={handleOpenLogOutModal}
          >
            {lang('Exit all wallets')}
            <i className={buildClassName('icon-chevron-right', styles.detailsIcon)} aria-hidden />
          </span>
        </div>
      )}

      {renderFooterButtons()}

      {operationType === 'unlock' && (
        <LogOutModal isOpen={isLogOutModalOpened} onClose={closeLogOutModal} isInAppLock />
      )}
    </div>
  );
}

function useCanAutoConfirm(enclaveSessionValidUntil?: number, isDisabled?: boolean) {
  if (isDisabled) return false;

  const autoConfirmTtl = enclaveSessionValidUntil ? enclaveSessionValidUntil - Date.now() : undefined;
  const [canAutoConfirm, setCanAutoConfirm] = useState(
    autoConfirmTtl !== undefined && autoConfirmTtl > 0,
  );

  useEffect(() => {
    const currentTtl = enclaveSessionValidUntil ? enclaveSessionValidUntil - Date.now() : undefined;
    const isValid = currentTtl !== undefined && currentTtl > 0;
    setCanAutoConfirm(isValid);
  }, [enclaveSessionValidUntil]);

  useTimeout(() => {
    setCanAutoConfirm(false);
  }, autoConfirmTtl && autoConfirmTtl > 0 ? autoConfirmTtl : undefined);

  return canAutoConfirm;
}

export default memo(withGlobal<OwnProps>((global): StateProps => {
  const { isPasswordNumeric, isAutoConfirmEnabled } = global.settings;
  const enclaveSessionValidUntil = global.enclaveSession?.validUntil;
  const isBiometricAuthEnabled = selectIsBiometricAuthEnabled(global);

  return {
    isPasswordNumeric,
    isAutoConfirmEnabled,
    enclaveSessionValidUntil,
    isBiometricAuthEnabled,
    authUsageCountRequest: selectAuthUsageCountRequest(global),
  };
})(PasswordForm));
